import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type {
  ChatRoomStateType,
  FetchChatRoomMessagesPayloadType,
} from 'features/Account/@types/chatChannel'
import { callErrorMsg } from 'helpers/errorMsg'
import { getChatRoomMessages } from '../../services/chatChannel'

const PAGE_LIMIT = 20

const initialState: ChatRoomStateType = {
  isLoading: false,
  isLoadingMore: false,
  requestId: '',
  pollRequestId: '',
  participants: [],
  messages: [],
  nextCursor: '',
  hasNextPage: false,
}

export const SLICE_NAME = 'chatRoom'

const chatRoomSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchChatRoomMessages.pending, (state, action) => {
        const { isLoadMore = false } = action.meta.arg
        state.requestId = action.meta.requestId
        state.isLoading = !isLoadMore
        state.isLoadingMore = isLoadMore
      })
      .addCase(fetchChatRoomMessages.rejected, (state, action) => {
        // a newer request has replaced this one
        if (state.requestId !== action.meta.requestId) return

        state.isLoading = false
        state.isLoadingMore = false
      })
      .addCase(fetchChatRoomMessages.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return

        const { room, pagination } = action.payload.data
        const messages = action.payload.data.messages || []
        state.isLoading = false
        state.isLoadingMore = false
        state.participants = room.participant || []
        state.messages = action.meta.arg.isLoadMore ? [...state.messages, ...messages] : messages
        state.nextCursor = pagination.next_cursor
        state.hasNextPage = pagination.next_page_exist
      })
      .addCase(pollChatRoomMessages.pending, (state, action) => {
        state.pollRequestId = action.meta.requestId
      })
      .addCase(pollChatRoomMessages.fulfilled, (state, action) => {
        // ignore polls from a room that was already closed or a newer poll
        if (state.pollRequestId !== action.meta.requestId) return

        const { room } = action.payload.data
        const latestMessages = action.payload.data.messages || []
        const messageIds = new Set(state.messages.map(({ id }) => id))
        const newMessages = latestMessages.filter(({ id }) => !messageIds.has(id))

        state.participants = room.participant || state.participants
        // keeps the older pages already loaded, only new messages are added
        if (newMessages.length > 0) state.messages = [...newMessages, ...state.messages]
      })
  },
})

export default chatRoomSlice.reducer
export const { reset } = chatRoomSlice.actions

export const fetchChatRoomMessages = createAsyncThunk(
  `${SLICE_NAME}/fetchChatRoomMessages`,
  async (
    { roomId, isLoadMore = false }: FetchChatRoomMessagesPayloadType,
    { rejectWithValue, getState },
  ) => {
    const {
      chatRoom: { nextCursor },
    } = getState() as StoreStateType

    try {
      const res = await getChatRoomMessages({
        roomId,
        params: {
          limit: PAGE_LIMIT,
          ...(isLoadMore && { direction: 'next', cursor: nextCursor }),
        },
      })
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

// refreshes the latest page in the background without showing the loader
export const pollChatRoomMessages = createAsyncThunk(
  `${SLICE_NAME}/pollChatRoomMessages`,
  async ({ roomId }: Pick<FetchChatRoomMessagesPayloadType, 'roomId'>, { rejectWithValue }) => {
    try {
      const res = await getChatRoomMessages({ roomId, params: { limit: PAGE_LIMIT } })
      return res.data
    } catch (error) {
      // silent, the next poll retries
      return rejectWithValue(error)
    }
  },
  {
    condition: (_, { getState }) => {
      const { isLoading, isLoadingMore } = (getState() as StoreStateType).chatRoom
      return !isLoading && !isLoadingMore && !document.hidden
    },
  },
)
